'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Add01Icon,
  Delete02Icon,
  ViewIcon,
  ViewOffIcon,
} from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { BodyDefault } from '../../../components/typography';
import { toasterApi } from '../../toast/ToastProvider';
import { SettingsBooleanControl } from '../../settings/SettingsBooleanControl';
import {
  buildGlobalHeadersSaveBody,
  hasGlobalHeaderChanges,
  removeVisibleHeaderIndex,
  toggleVisibleHeaderIndex,
  toGlobalHeaderEntries,
  type GlobalHeaderEntry,
  type GlobalHeaderSource,
  type GlobalHeadersSaveBody,
} from './global-headers.model';

export type ApplicationGlobalHeadersEditorProps = {
  globalHeaders: readonly GlobalHeaderSource[] | null | undefined;
  limitGlobalHeadersByScope: boolean;
  isReadOnly?: boolean;
  isSaving?: boolean;
  onSave: (body: GlobalHeadersSaveBody) => Promise<unknown>;
};

export function ApplicationGlobalHeadersEditor({
  globalHeaders,
  limitGlobalHeadersByScope,
  isReadOnly,
  isSaving,
  onSave,
}: ApplicationGlobalHeadersEditorProps) {
  const t = useTranslations('designSystem.applicationSettings.networking');
  const tCommon = useTranslations('designSystem.common');
  const [headers, setHeaders] = useState<GlobalHeaderEntry[]>(() =>
    toGlobalHeaderEntries(globalHeaders),
  );
  const [limitByScope, setLimitByScope] = useState(limitGlobalHeadersByScope);
  const [visibleIndexes, setVisibleIndexes] = useState<Set<number>>(
    () => new Set(),
  );

  useEffect(() => {
    setHeaders(toGlobalHeaderEntries(globalHeaders));
  }, [globalHeaders]);
  useEffect(() => {
    setLimitByScope(limitGlobalHeadersByScope);
  }, [limitGlobalHeadersByScope]);

  const hasChanges = hasGlobalHeaderChanges({
    headers,
    limitByScope,
    originalHeaders: globalHeaders,
    originalLimitByScope: limitGlobalHeadersByScope,
  });
  const updateHeader = ({
    index,
    field,
    value,
  }: {
    index: number;
    field: keyof GlobalHeaderEntry;
    value: string;
  }) => {
    setHeaders((previous) =>
      previous.map((header, headerIndex) =>
        headerIndex === index ? { ...header, [field]: value } : header,
      ),
    );
  };

  const removeHeader = (index: number) => {
    setHeaders((previous) =>
      previous.filter((_, headerIndex) => headerIndex !== index),
    );
    setVisibleIndexes((previous) =>
      removeVisibleHeaderIndex({
        visibleIndexes: previous,
        removedIndex: index,
      }),
    );
  };

  const toggleVisibility = (index: number) => {
    setVisibleIndexes((previous) =>
      toggleVisibleHeaderIndex({ visibleIndexes: previous, index }),
    );
  };

  const handleSave = async () => {
    try {
      await onSave(buildGlobalHeadersSaveBody({ headers, limitByScope }));
      toasterApi.success({ title: t('saveSuccess') });
    } catch (error) {
      console.error(new Error('Failed to update global headers'), error);
      toasterApi.error({ title: t('saveError') });
    }
  };

  return (
    <VStack gap={3} alignItems="stretch" pt={4}>
      <BodyDefault color="fg.muted">{t('globalHeadersHelp')}</BodyDefault>
      <VStack gap={2} alignItems="stretch">
        <BodyDefault>{t('limitHeadersByScope')}</BodyDefault>
        <SettingsBooleanControl
          checked={limitByScope}
          label={limitByScope ? tCommon('yes') : tCommon('no')}
          disabled={isReadOnly}
          onCheckedChange={({ checked }) => setLimitByScope(checked)}
        />
        <BodyDefault color="fg.muted">
          {t('limitHeadersByScopeHelp')}
        </BodyDefault>
      </VStack>
      {headers.map((header, index) => {
        const isValueVisible = visibleIndexes.has(index);

        return (
          <HStack key={index} gap={2} w="full">
            <Input
              placeholder={t('headerNamePlaceholder')}
              value={header.key}
              onChange={(event) =>
                updateHeader({ index, field: 'key', value: event.target.value })
              }
              disabled={isReadOnly}
              flex={1}
            />
            <Input
              placeholder={t('headerValuePlaceholder')}
              type={isValueVisible ? 'text' : 'password'}
              value={header.value}
              onChange={(event) =>
                updateHeader({
                  index,
                  field: 'value',
                  value: event.target.value,
                })
              }
              disabled={isReadOnly}
              flex={2}
            />
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={isValueVisible ? t('hideValue') : t('showValue')}
              onClick={() => toggleVisibility(index)}
            >
              {isValueVisible ? (
                <ViewOffIcon size={16} />
              ) : (
                <ViewIcon size={16} />
              )}
            </IconButton>
            {!isReadOnly ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('removeHeader')}
                onClick={() => removeHeader(index)}
              >
                <Delete02Icon size={16} />
              </IconButton>
            ) : null}
          </HStack>
        );
      })}
      {!isReadOnly ? (
        <HStack gap={2}>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setHeaders((previous) => [...previous, { key: '', value: '' }])
            }
            alignSelf="flex-start"
          >
            <Add01Icon size={16} />
            {t('addHeader')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              void handleSave();
            }}
            disabled={!hasChanges}
            loading={isSaving}
          >
            {tCommon('saveChanges')}
          </Button>
        </HStack>
      ) : null}
    </VStack>
  );
}
